// src/components/admin/EmailTrackingDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Mail, ShoppingCart, Zap, PieChart, ArrowUpRight, RefreshCw } from 'lucide-react';
import { Spinner } from '../ui/spinner';

const EmailTrackingDashboard = () => {
  const [stats, setStats] = useState({
    initialEmails: 0,
    fullEmails: 0,
    totalEmails: 0,
    purchases: 0,
    revenue: 0,
    conversionRate: 0,
    agentCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('week'); // 'day', 'week', 'month', 'all'

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchEmailStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Make API call to get email stats with timeRange parameter
      const response = await apiClient.get(`/admin/email/stats?timeRange=${timeRange}`);
      
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load email statistics');
        // Use placeholder data for display
        setStats({
          initialEmails: 152,
          fullEmails: 38,
          totalEmails: 190,
          purchases: 38,
          revenue: 5700,
          conversionRate: 25,
          agentCount: 12
        });
      }
    } catch (err) {
      console.error('Error fetching email stats:', err);
      setError('Failed to load email statistics');
      // Use placeholder data for display
      setStats({
        initialEmails: 152,
        fullEmails: 38,
        totalEmails: 190,
        purchases: 38,
        revenue: 5700,
        conversionRate: 25,
        agentCount: 12
      });
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchEmailStats();
  }, [fetchEmailStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold">Email & Conversion Tracking</h2>
        
        <div className="flex items-center space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
          
          <button
            onClick={fetchEmailStats}
            className="p-2 text-gray-600 hover:text-gray-900"
            disabled={loading}
          >
            {loading ? (
              <Spinner className="h-5 w-5" />
            ) : (
              <RefreshCw className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Emails Sent Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Emails Sent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Mail className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.totalEmails}</div>
                <div className="text-xs text-gray-500 flex items-center">
                  <span className="flex items-center text-green-600 mr-1">
                    <ArrowUpRight className="h-3 w-3 mr-1" />
                    12%
                  </span>
                  vs previous period
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-between text-sm">
              <div>
                <div className="text-gray-500">Initial</div>
                <div className="font-medium">{stats.initialEmails}</div>
              </div>
              <div>
                <div className="text-gray-500">Full Details</div>
                <div className="font-medium">{stats.fullEmails}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Purchases Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Purchases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-full mr-3">
                <ShoppingCart className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.purchases}</div>
                <div className="text-xs text-gray-500 flex items-center">
                  <span className="flex items-center text-green-600 mr-1">
                    <ArrowUpRight className="h-3 w-3 mr-1" />
                    5%
                  </span>
                  vs previous period
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-gray-500 text-sm">Total Revenue</div>
              <div className="font-medium text-xl">{formatCurrency(stats.revenue)}</div>
            </div>
          </CardContent>
        </Card>
        
        {/* Conversion Rate Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Conversion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-full mr-3">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.conversionRate}%</div>
                <div className="text-xs text-gray-500 flex items-center">
                  <span className="flex items-center text-green-600 mr-1">
                    <ArrowUpRight className="h-3 w-3 mr-1" />
                    2%
                  </span>
                  vs previous period
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-purple-600 h-2.5 rounded-full" 
                  style={{ width: `${Math.min(stats.conversionRate, 100)}%` }}
                ></div>
              </div>
              <div className="mt-1 text-xs text-gray-500">Target: 30%</div>
            </div>
          </CardContent>
        </Card>
        
        {/* Active Agents Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Active Agents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-full mr-3">
                <PieChart className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.agentCount}</div>
                <div className="text-xs text-gray-500 flex items-center">
                  <span className="flex items-center text-green-600 mr-1">
                    <ArrowUpRight className="h-3 w-3 mr-1" />
                    3
                  </span>
                  new this period
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-gray-500 text-sm">Average Purchases Per Agent</div>
              <div className="font-medium text-xl">
                {stats.agentCount ? (stats.purchases / stats.agentCount).toFixed(1) : '0.0'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* More detailed stats could go here - charts, tables, etc. */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Purchases</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Spinner className="h-8 w-8 text-primary" />
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <ShoppingCart className="h-12 w-12 mx-auto text-gray-400" />
                <p className="mt-2">No recent purchases to display</p>
                <p className="text-sm">Recent purchase data will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Email Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Spinner className="h-8 w-8 text-primary" />
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Mail className="h-12 w-12 mx-auto text-gray-400" />
                <p className="mt-2">Email performance data will appear here</p>
                <p className="text-sm">Including open rates, click rates, and delivery metrics</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailTrackingDashboard;